import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NAV_ITEMS = [
  { labelKey: 'nav.home', path: '/' },
  { labelKey: 'nav.solutions', path: '/solutions' },
  { labelKey: 'nav.howWeWork', path: '/how-we-work' },
  { labelKey: 'nav.about', path: '/about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isSpanish = i18n.language?.startsWith('es');

  const toggleLanguage = () => {
    const next = isSpanish ? 'en' : 'es';
    i18n.changeLanguage(next);
    localStorage.setItem('morpheniq-lang', next);
    document.documentElement.lang = next;
  };

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-primary-500 border-b border-white/10"
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-14 md:h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0 group -mt-3 md:-mt-4 lg:-mt-5">
              <div className="relative p-1 rounded-xl transition-all duration-300">
                <img
                  src="https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/ed6231ec-ad64-40b6-9b06-9ed759a36c3f_compressed_ese.webp"
                  alt="MORPHENIQ"
                  className="h-14 md:h-16 lg:h-20 w-auto transition-transform duration-500 group-hover:scale-105 drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]"
                />
                {/* Glow behind logo */}
                <div className="absolute inset-0 -z-10 bg-background-50/20 rounded-2xl blur-2xl scale-125 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    location.pathname === item.path
                      ? 'text-accent-400'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA + Language */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-colors whitespace-nowrap"
                aria-label={isSpanish ? 'Switch to English' : 'Cambiar a español'}
              >
                <i className="ri-global-line" />
                {isSpanish ? 'EN' : 'ES'}
              </button>
              <Link
                to="/shape-your-project"
                className="px-5 py-2.5 bg-secondary-500 text-foreground-950 text-sm font-semibold rounded-md hover:bg-secondary-400 transition-colors whitespace-nowrap"
              >
                {t('cta.shapeYourProject')}
              </Link>
              <Link
                to="/book-meeting"
                className="px-5 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-md hover:bg-purple-500 transition-colors whitespace-nowrap"
              >
                {t('cta.letsMeet')}
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-12 h-12 flex items-center justify-center rounded-md text-white/80 hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/20 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bg-primary-500 border-b border-white/10 shadow-lg">
            <nav className="px-4 py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 text-base font-medium rounded-md transition-colors ${
                    location.pathname === item.path
                      ? 'text-accent-400 bg-white/5'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {t(item.labelKey)}
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-white/10 flex flex-col gap-2">
                <button
                  onClick={toggleLanguage}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 text-base font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-md transition-colors"
                >
                  <i className="ri-global-line" />
                  {isSpanish ? 'English' : 'Español'}
                </button>
                <Link
                  to="/shape-your-project"
                  className="block w-full text-center px-5 py-3 bg-secondary-500 text-foreground-950 text-base font-semibold rounded-md hover:bg-secondary-400 transition-colors"
                >
                  {t('cta.shapeYourProject')}
                </Link>
                <Link
                  to="/book-meeting"
                  className="block w-full text-center px-5 py-3 bg-purple-600 text-white text-base font-semibold rounded-md hover:bg-purple-500 transition-colors"
                >
                  {t('cta.letsMeet')}
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}